import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { faro } from '@grafana/faro-web-sdk';
import { API_CONFIG } from '../config/api.config';
import { TokenManager } from '../utils/tokenManager';
import { getDistinctId } from './analytics/posthog';
import { logger } from '../utils/logger';

/**
 * Get the current Faro session ID for request correlation.
 * Returns null if Faro is not initialized or no session exists.
 */
function getFaroSessionId(): string | null {
  try {
    return faro?.api?.getSession()?.id ?? null;
  } catch {
    return null;
  }
}

// Token refresh state management
let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

/**
 * Subscribe to token refresh completion.
 * Returns a promise that resolves with true if refresh succeeded.
 */
function subscribeToTokenRefresh(): Promise<boolean> {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });
}

/**
 * Notify all subscribers that token refresh is complete.
 */
function onTokenRefreshComplete(success: boolean): void {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
}

/**
 * Perform token refresh with proper locking and subscriber notification.
 * All concurrent requests wait for the same refresh operation.
 */
async function refreshTokenWithLock(): Promise<boolean> {
  if (isRefreshing) {
    // Already refreshing - wait for completion
    return subscribeToTokenRefresh();
  }

  isRefreshing = true;

  try {
    const success = await TokenManager.refreshAccessToken();
    onTokenRefreshComplete(success);
    return success;
  } catch (error) {
    logger.warn('[authenticatedApi] Token refresh failed:', { error });
    onTokenRefreshComplete(false);
    return false;
  } finally {
    isRefreshing = false;
  }
}

// Create axios instance with cookie-based authentication
export const api = axios.create({
  baseURL: API_CONFIG.apiUrl,
  timeout: API_CONFIG.timeout,
  withCredentials: true, // Send httpOnly cookies with every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add CSRF token and tenant ID
api.interceptors.request.use(
  async (config) => {
    // Add CSRF token from the non-httpOnly __csrf cookie
    const csrfToken = TokenManager.getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Add tenant ID header for multitenancy support
    const selectedTenantId = TokenManager.getTenantId();
    const fallbackTenantId = '00000000-0000-0000-0000-000000000000';
    const normalizedTenantId =
      selectedTenantId && selectedTenantId.trim() ? selectedTenantId.trim() : fallbackTenantId;

    if (!config.headers['X-Tenant-ID']) {
      config.headers['X-Tenant-ID'] = normalizedTenantId;
    }

    // Add PostHog distinct_id for backend analytics correlation
    const distinctId = getDistinctId();
    if (distinctId) {
      config.headers['X-PostHog-Distinct-ID'] = distinctId;
    }

    // Add Faro session ID for distributed tracing correlation
    const faroSessionId = getFaroSessionId();
    if (faroSessionId) {
      config.headers['X-Faro-Session-ID'] = faroSessionId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/** Shape of structured error responses from the backend */
interface ApiErrorData {
  error?: string;
  code?: string;
  message?: string;
  provider?: string;
}

/**
 * Check if this is an OAuth token error (provider token expired).
 * These errors come from repository/provider operations, not JWT auth.
 */
function isOAuthTokenError(error: AxiosError<ApiErrorData>): boolean {
  const errorMessage = error.response?.data?.error;
  const errorCode = error.response?.data?.code;

  // OAuth errors have specific error messages from the server
  // These indicate provider token issues, not JWT issues
  return (
    errorMessage === 'token_expired' ||
    errorMessage === 'token_validation_failed' ||
    errorMessage === 'connection_required' ||
    errorCode === 'OAUTH_TOKEN_EXPIRED' ||
    errorCode === 'PROVIDER_TOKEN_INVALID'
  );
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Check for token refresh header from server
    if (response.headers['x-token-refresh-needed'] === 'true') {
      // Token is expiring soon, trigger refresh in background
      // This is proactive - don't block the response
      refreshTokenWithLock().catch((err) => {
        logger.warn('[authenticatedApi] Background token refresh failed:', { error: err });
      });
    }
    return response;
  },
  async (error: AxiosError<ApiErrorData>) => {
    const statusCode = error.response?.status;
    const errorData = error.response?.data;
    const errorCode = errorData?.code;
    const provider = errorData?.provider;
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // Handle CSRF token expiration - refresh tokens to get a new CSRF cookie
    if (
      statusCode === 403 &&
      errorData?.error === 'CSRF validation failed' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        await refreshTokenWithLock();
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    // Handle email verification required errors (403 with EMAIL_NOT_VERIFIED)
    if (statusCode === 403 && errorCode === 'EMAIL_NOT_VERIFIED') {
      window.dispatchEvent(
        new CustomEvent('auth:email-verification-required', {
          detail: {
            message: errorData?.message || 'Please verify your email address',
          },
        }),
      );
      return Promise.reject(error);
    }

    // Handle OAuth token expiration errors (provider tokens, not JWT)
    // These require user action to reconnect the provider
    if (statusCode === 401 && isOAuthTokenError(error)) {
      logger.warn('[authenticatedApi] OAuth provider token expired for:', { provider });
      window.dispatchEvent(
        new CustomEvent('oauth:token-expired', {
          detail: {
            provider,
            message: errorData?.message || `Your ${provider} connection has expired`,
            errorCode: errorCode ?? errorData?.error,
          },
        }),
      );
      return Promise.reject(error);
    }

    // Handle JWT authentication errors (user session)
    if (statusCode === 401 && !originalRequest._retry) {
      logger.warn('[authenticatedApi] 401 Unauthorized, attempting token refresh');
      originalRequest._retry = true;

      try {
        // Use the locked refresh mechanism - all concurrent 401s wait for the same refresh
        const success = await refreshTokenWithLock();

        if (success) {
          // Retry the original request - new cookies are set automatically
          return api(originalRequest);
        } else {
          logger.error('[authenticatedApi] Token refresh failed, logging out');
          TokenManager.clearTokens();
          window.dispatchEvent(new CustomEvent('auth:required'));
        }
      } catch (refreshError) {
        // Refresh threw an error - check if we should retry or give up
        logger.error('[authenticatedApi] Token refresh failed:', { error: refreshError });
        TokenManager.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:required'));
      }
    } else if (statusCode === 401 && originalRequest._retry) {
      // Already retried once and still got 401 - give up
      logger.error('[authenticatedApi] 401 after retry, clearing auth state');
      TokenManager.clearTokens();
      window.dispatchEvent(new CustomEvent('auth:required'));
    } else if (!error.response) {
      // Network error (no response) - keep cookies for manual retry
      logger.warn('[authenticatedApi] Network error');
    } else {
      // Other HTTP errors (4xx, 5xx) - let caller handle
      logger.warn('[authenticatedApi] HTTP error', { statusCode });
    }

    return Promise.reject(error);
  },
);

export default api;
